// Review prompt popup (2026-10-01, Jaime): star rating + optional text.
// Spanish-only copy (no global i18n layer yet — same approach as InstallPrompt).
// States: form → submitting → success ('¡Gracias!') / already (409) / error.
// Dismiss paths call the backend so the user is never re-prompted
// ('dismissed' = permanent, 'remind_later' = 7-day re-arm server-side).
import React, { useEffect, useState } from 'react';
import { Star, X } from 'lucide-react';
import { reviewsAPI } from '../services/reviewsApi';

type Phase = 'form' | 'submitting' | 'success' | 'already' | 'error';

const MAX_TEXT = 2000;

const RATING_LABELS: Record<number, string> = {
  1: 'Muy malo',
  2: 'Malo',
  3: 'Regular',
  4: 'Bueno',
  5: '¡Excelente!',
};

export const ReviewPromptModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [errorMsg, setErrorMsg] = useState('');

  // Success auto-closes after a beat so the "¡Gracias!" is seen, not clicked.
  useEffect(() => {
    if (phase !== 'success' && phase !== 'already') return;
    const t = window.setTimeout(onClose, phase === 'success' ? 3000 : 2500);
    return () => window.clearTimeout(t);
  }, [phase, onClose]);

  if (!open) return null;

  const handleDismiss = (action: 'dismissed' | 'remind_later') => {
    reviewsAPI.promptDismiss(action).catch(() => {}); // best-effort; popup closes regardless
    onClose();
  };

  const submit = async () => {
    if (!rating || phase !== 'form') return;
    setPhase('submitting');
    try {
      await reviewsAPI.submitReview(rating, text);
      setPhase('success');
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setPhase('already');
      } else {
        setErrorMsg(e?.response?.data?.error || 'No se pudo enviar tu reseña. Inténtalo de nuevo.');
        setPhase('error');
      }
    }
  };

  const shown = hover || rating;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Deja tu reseña de Vecindus"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-[var(--v-surface-0,#fff)] p-6 shadow-xl">
        <button
          onClick={() => handleDismiss('dismissed')}
          aria-label="Cerrar"
          className="absolute right-3 top-3 rounded-full p-1.5 text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {phase === 'success' || phase === 'already' ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex justify-center gap-1">
              {phase === 'success' &&
                Array.from({ length: 5 }, (_, i) => (
                  <Star key={i} className={`h-8 w-8 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-[var(--v-text-secondary)]'}`} />
                ))}
            </div>
            <h2 className="text-xl font-bold text-[var(--v-text-primary)]">
              {phase === 'success' ? '¡Gracias!' : 'Ya nos habías enviado tu reseña'}
            </h2>
            <p className="mt-2 text-sm text-[var(--v-text-secondary)]">
              {phase === 'success'
                ? 'Tu reseña se publicará tras una breve revisión. ¡Nos alegra que uses Vecindus!'
                : 'Solo se permite una reseña por persona.'}
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-bold text-[var(--v-text-primary)]">¿Cómo va tu experiencia con Vecindus?</h2>
            <p className="mt-1 text-sm text-[var(--v-text-secondary)]">
              Tu opinión nos ayuda a mejorar la app para toda la comunidad.
            </p>

            <div className="mt-5 flex justify-center gap-2" role="radiogroup" aria-label="Puntuación">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} ${n === 1 ? 'estrella' : 'estrellas'}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="rounded-full p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--v-teal-500,#14b8a6)]"
                >
                  <Star className={`h-8 w-8 transition-colors ${n <= shown ? 'fill-amber-400 text-amber-400' : 'text-[var(--v-text-secondary)]'}`} />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="mt-1 text-center text-sm font-medium text-[var(--v-teal-700)]">{RATING_LABELS[rating]}</p>
            )}

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
              maxLength={MAX_TEXT}
              rows={3}
              placeholder="Cuéntanos más (opcional)"
              className="mt-4 w-full resize-none rounded-xl border border-[var(--v-border,#e5e7eb)] bg-[var(--v-surface-1)] p-3 text-sm text-[var(--v-text-primary)] placeholder:text-[var(--v-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-500,#14b8a6)]"
            />
            <p className="mt-1 text-right text-xs text-[var(--v-text-secondary)]">{text.length}/{MAX_TEXT}</p>

            {phase === 'error' && (
              <p className="mt-2 text-center text-sm text-red-600" role="alert">{errorMsg}</p>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={submit}
                disabled={!rating || phase === 'submitting'}
                className="w-full rounded-full bg-[var(--v-teal-600,#0d9488)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--v-teal-700,#0f766e)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === 'submitting' ? 'Enviando…' : 'Enviar reseña'}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDismiss('remind_later')}
                  className="flex-1 rounded-full border border-[var(--v-border,#e5e7eb)] py-2 text-sm font-medium text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Recordármelo más tarde
                </button>
                <button
                  onClick={() => handleDismiss('dismissed')}
                  className="flex-1 rounded-full py-2 text-sm font-medium text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  No, gracias
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
